import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import VehicleForm from '../components/VehicleForm';
import { confirmDialog } from '../utils/confirm';
import EventForm from '../components/EventForm';
import DocumentForm from '../components/DocumentForm';
import AccidentForm from '../components/AccidentForm';
import CostForm from '../components/CostForm';
import Modal from '../components/Modal';
import VehicleStatsContent from '../components/VehicleStatsContent';
import { companyKey, groupCostsByCompany, shortCompanyLabels } from '../utils/company';
import { COST_TYPE_COLORS, costTypeColor } from '../utils/costTypes';


const fmtAmt = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return `${Math.round(n)}`;
};

const CostsCard = ({ costs, onAdd }) => {
  const totalRON = costs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const preview = costs.slice(0, 6);
  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Vehicle Costs</div>
        <button className="btn btn-primary btn-sm" onClick={onAdd}>+ Add</button>
      </div>
      <div className="card-body">
        {costs.length === 0 ? (
          <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px' }}>No costs recorded yet.</div>
        ) : (
          <>
            {preview.map((cost, i) => {
              const color = costTypeColor(cost.costType);
              return (
                <div key={cost._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < preview.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', textTransform: 'capitalize' }}>{cost.costType || 'Cost'}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', paddingLeft: '14px' }}>
                      {cost.date ? new Date(cost.date).toLocaleDateString() : '—'}
                      {cost.description ? ` • ${cost.description.slice(0, 28)}${cost.description.length > 28 ? '…' : ''}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#0077b6' }}>{Number(cost.amount || 0).toLocaleString()}</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-3)', marginLeft: '3px' }}>{cost.currency || 'RON'}</span>
                  </div>
                </div>
              );
            })}
            {costs.length > 6 && (
              <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{costs.length - 6} more</div>
            )}
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)' }}>{costs.length} entr{costs.length === 1 ? 'y' : 'ies'}</span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-1)' }}>{totalRON.toLocaleString()} <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-3)' }}>RON</span></span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DonutChart = ({ segments, size = 90, strokeWidth = 11, activeIndex = -1 }) => {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);
  const r = (size - strokeWidth) / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((s, g) => s + (g.value || 0), 0);
  let cum = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef2f7" strokeWidth={strokeWidth} />
      {total > 0 && segments.map((seg, i) => {
        const dash = (seg.value / total) * circ;
        const dashoffset = circ / 4 - cum;
        cum += dash;
        const isActive = activeIndex === i;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeDasharray={ready ? `${dash} ${circ - dash}` : `0 ${circ}`}
            strokeDashoffset={dashoffset}
            strokeLinecap="butt"
            style={{
              transition: `stroke-dasharray 0.85s cubic-bezier(0.4,0,0.2,1) ${i * 0.15}s, opacity 0.2s ease, filter 0.2s ease`,
              opacity: activeIndex >= 0 && !isActive ? 0.22 : 1,
              filter: isActive ? `drop-shadow(0 0 7px ${seg.color})` : 'none',
            }}
          />
        );
      })}
    </svg>
  );
};

const CompanyCostChart = ({ companyData, allTypes, grandTotal, onCompanyClick }) => {
  const [ready, setReady] = React.useState(false);
  const [hoveredIdx, setHoveredIdx] = React.useState(-1);
  React.useEffect(() => { const t = setTimeout(() => setReady(true), 120); return () => clearTimeout(t); }, []);
  const CHART_H = 180;
  const maxTotal = Math.max(...companyData.map((d) => d.total), 1);
  const yLabels = [maxTotal, maxTotal * 0.75, maxTotal * 0.5, maxTotal * 0.25, 0].map((v) => Math.round(v));

  // Two-word labels, lengthened only where two companies would otherwise
  // read the same (e.g. "Corporate Intelligence Agency" / "... Advisors").
  const shortLabels = shortCompanyLabels(companyData.map((d) => d.name));

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header" style={{ flexWrap: 'wrap', gap: '10px', borderBottom: '1px solid #f1f5f9' }}>
        <div>
          <div className="card-title">Costs by Company</div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {allTypes.map((t) => (
            <span key={t} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--text-2)', fontWeight: 500, padding: '3px 8px', borderRadius: '999px', background: `${COST_TYPE_COLORS[t] || '#9ca3af'}12` }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: COST_TYPE_COLORS[t] || '#9ca3af', flexShrink: 0 }} />
              {t}
            </span>
          ))}
        </div>
      </div>
      <div className="card-body" style={{ paddingBottom: '12px', paddingTop: '16px', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: '0', alignItems: 'stretch', minWidth: `${Math.max(companyData.length * 72, 220)}px` }}>
          {/* Y-axis */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingBottom: '52px', paddingRight: '10px', flexShrink: 0, minWidth: '32px' }}>
            {yLabels.map((v) => (
              <span key={v} style={{ fontSize: '10px', color: '#b0bec5', lineHeight: 1, textAlign: 'right', fontWeight: 500 }}>{fmtAmt(v)}</span>
            ))}
          </div>
          {/* Chart area */}
          <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
            {/* Grid lines */}
            {[0, 25, 50, 75, 100].map((pct) => (
              <div key={pct} style={{ position: 'absolute', left: 0, right: 0, bottom: `calc(52px + ${(pct / 100) * CHART_H}px)`, height: pct === 0 ? '2px' : '1px', background: pct === 0 ? '#e5e7eb' : '#f0f4f8', zIndex: 0, borderRadius: '1px' }} />
            ))}
            {/* Bars */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: `${CHART_H + 52}px`, paddingBottom: '52px', position: 'relative', zIndex: 1 }}>
              {companyData.map(({ name, total, byType }, idx) => {
                const isHovered = hoveredIdx === idx;
                const barH = ready && total > 0 ? Math.max((total / maxTotal) * CHART_H, 6) : 0;
                const typeEntries = Object.entries(byType).sort((a, b) => b[1] - a[1]);
                const shortName = shortLabels.get(name) || name;
                const dominantColor = typeEntries[0] ? (COST_TYPE_COLORS[typeEntries[0][0]] || '#0077b6') : '#0077b6';
                return (
                  <div
                    key={name}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', cursor: total > 0 ? 'pointer' : 'default', transition: 'transform 0.2s cubic-bezier(0.34,1.56,0.64,1)', transform: isHovered && total > 0 ? 'translateY(-5px)' : 'none' }}
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(-1)}
                    onClick={total > 0 ? () => onCompanyClick(name) : undefined}
                  >
                    {/* Amount badge */}
                    <div style={{ height: '22px', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {total > 0 && (
                        <span style={{ fontSize: '11px', fontWeight: 800, color: isHovered ? dominantColor : '#64748b', background: isHovered ? `${dominantColor}15` : 'transparent', padding: '2px 6px', borderRadius: '6px', whiteSpace: 'nowrap', transition: 'all 0.2s ease' }}>
                          {total < 10000 ? total.toLocaleString() : fmtAmt(total)}
                        </span>
                      )}
                    </div>
                    {/* Stacked bar */}
                    <div style={{ width: '100%', maxWidth: '60px', height: `${barH}px`, position: 'relative', borderRadius: '6px 6px 0 0', overflow: 'hidden', background: total === 0 ? '#f1f5f9' : undefined, transition: 'height 1s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease', boxShadow: isHovered && total > 0 ? `0 -6px 20px ${dominantColor}50` : 'none', alignSelf: 'center' }}>
                      {(() => {
                        let bottom = 0;
                        return typeEntries.map(([type, amt]) => {
                          const segH = barH > 0 ? (amt / total) * barH : 0;
                          const b = bottom;
                          bottom += segH;
                          const color = COST_TYPE_COLORS[type] || '#9ca3af';
                          return <div key={type} style={{ position: 'absolute', bottom: `${b}px`, left: 0, right: 0, height: `${segH}px`, background: color, transition: 'filter 0.2s ease', filter: isHovered ? 'brightness(1.1) saturate(1.2)' : 'none' }} />;
                        });
                      })()}
                      {isHovered && total > 0 && (
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(255,255,255,0.18) 0%,rgba(255,255,255,0) 60%)', pointerEvents: 'none', borderRadius: '6px 6px 0 0' }} />
                      )}
                    </div>
                    {/* X label */}
                    <div style={{ marginTop: '8px', fontSize: '10px', fontWeight: total > 0 ? 700 : 400, textAlign: 'center', color: isHovered && total > 0 ? dominantColor : (total > 0 ? 'var(--text-1)' : '#cbd5e1'), lineHeight: 1.35, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'color 0.2s ease', padding: '0 2px' }}>
                      {shortName}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChartCard = ({ title, borderColor, segments, center, legend, onClick }) => {
  const [hoveredIdx, setHoveredIdx] = React.useState(-1);
  const activeItem = hoveredIdx >= 0 ? legend[hoveredIdx] : null;
  return (
    <div
      className="stat-card"
      style={{ padding: '18px 20px', borderTop: `3px solid ${borderColor}`, cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.15s ease, transform 0.15s ease' }}
      onMouseLeave={() => setHoveredIdx(-1)}
      onClick={onClick}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; } : undefined}
      onMouseOut={onClick ? (e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; } : undefined}
    >
      <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#9ca3af', marginBottom: '14px' }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{ position: 'relative', width: 84, height: 84, flexShrink: 0 }}>
          <DonutChart segments={segments} size={84} strokeWidth={11} activeIndex={hoveredIdx} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ fontSize: activeItem ? '15px' : '20px', fontWeight: 800, lineHeight: 1, color: activeItem ? activeItem.color : (center.valueColor || 'var(--text-1)'), transition: 'all 0.18s ease' }}>
              {activeItem ? activeItem.value : center.value}
            </span>
            <span style={{ fontSize: '9px', color: activeItem ? activeItem.color : '#9ca3af', marginTop: '3px', fontWeight: activeItem ? 700 : 400, transition: 'all 0.18s ease' }}>
              {activeItem ? activeItem.label : center.label}
            </span>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {legend.map((item, i) => (
            <div
              key={item.label}
              onMouseEnter={() => setHoveredIdx(i)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '4px 7px', borderRadius: '7px',
                background: hoveredIdx === i ? `${item.color}18` : 'transparent',
                transform: hoveredIdx === i ? 'translateX(3px)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px', color: hoveredIdx === i ? 'var(--text-1)' : 'var(--text-2)', fontWeight: hoveredIdx === i ? 700 : 400, transition: 'all 0.15s ease' }}>
                <span style={{ width: hoveredIdx === i ? 9 : 7, height: hoveredIdx === i ? 9 : 7, borderRadius: '50%', background: item.color, flexShrink: 0, boxShadow: hoveredIdx === i ? `0 0 7px ${item.color}` : 'none', transition: 'all 0.15s ease' }} />
                {item.label}
              </span>
              <span style={{ fontSize: hoveredIdx === i ? '16px' : '13px', fontWeight: hoveredIdx === i ? 800 : 600, color: hoveredIdx === i ? item.color : 'var(--text-1)', transition: 'all 0.15s ease' }}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, searchQuery, isAdmin } = useAppContext();
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [recentVehicles, setRecentVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activeCompany, setActiveCompany] = useState('All');
  const [loading, setLoading] = useState(true);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showAccidentForm, setShowAccidentForm] = useState(false);
  const [showCostForm, setShowCostForm] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [vehicleCosts, setVehicleCosts] = useState([]);
  const [allCosts, setAllCosts] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showAllCostsSummary, setShowAllCostsSummary] = useState(false);

  useEffect(() => {
    if (!selectedVehicle?.plate) { setVehicleCosts([]); return; }
    apiEndpoints.costs.list()
      .then(res => {
        const sorted = (res.data || [])
          .filter(c => c.plate === selectedVehicle.plate)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setVehicleCosts(sorted);
      })
      .catch(() => setVehicleCosts([]));
  }, [selectedVehicle?.plate]);

  // A document saved with a renewal cost (DocumentForm) changes the totals shown here.
  useEffect(() => {
    const refetch = () => {
      apiEndpoints.costs.list()
        .then((res) => {
          const all = res.data || [];
          setAllCosts(all);
          if (selectedVehicle?.plate) {
            setVehicleCosts(all.filter((c) => c.plate === selectedVehicle.plate).sort((a, b) => new Date(b.date) - new Date(a.date)));
          }
        })
        .catch(() => {});
    };
    window.addEventListener('fleet:costs-changed', refetch);
    return () => window.removeEventListener('fleet:costs-changed', refetch);
  }, [selectedVehicle?.plate]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const [summaryRes, alertsRes, vehiclesRes, companiesRes] = await Promise.all([
          apiEndpoints.dashboard.summary(),
          apiEndpoints.events.list(),
          apiEndpoints.vehicles.list(),
          apiEndpoints.companies.list(),
        ]);

        setSummary(summaryRes.data);
        setAlerts(alertsRes.data);
        setRecentVehicles(vehiclesRes.data);
        setCompanies(companiesRes.data);

        // Fetch costs separately so a failure doesn't break the whole dashboard
        apiEndpoints.costs.list()
          .then((res) => setAllCosts(res.data || []))
          .catch(() => {});
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await apiEndpoints.documents.list();
        setDocuments(res.data || []);
      } catch (err) {
        console.error('Failed to load documents:', err);
      }
    };
    fetchDocuments();
  }, []);

  const companyCounts = companies.reduce((acc, company) => {
    // Backend companies use `label` (not `name`).
    acc[company.label] = recentVehicles.filter((v) => v.company === company.label).length;
    return acc;
  }, {});

  const filteredVehicles = activeCompany === 'All'
    ? recentVehicles.filter(vehicle => 
        !searchQuery || 
        vehicle.plate?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.model?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.company?.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 5)
    : recentVehicles.filter((vehicle) => 
        vehicle.company === activeCompany &&
        (!searchQuery || 
          vehicle.plate?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          vehicle.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          vehicle.model?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          vehicle.company?.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 5);

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setShowVehicleForm(true);
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowVehicleForm(true);
  };

  const handleSaveVehicle = async (vehicleData) => {
    try {
      if (editingVehicle) {
        await apiEndpoints.vehicles.update(editingVehicle.id, vehicleData);
      } else {
        await apiEndpoints.vehicles.create(vehicleData);
      }
      // Refresh dashboard data
      const [summaryRes, alertsRes, vehiclesRes, companiesRes] = await Promise.all([
        apiEndpoints.dashboard.summary(),
        apiEndpoints.events.list(),
        apiEndpoints.vehicles.list(),
        apiEndpoints.companies.list(),
      ]);
      setSummary(summaryRes.data);
      setAlerts(alertsRes.data);
      setRecentVehicles(vehiclesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error saving vehicle:', error);
      throw error;
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (await confirmDialog('Are you sure you want to delete this vehicle?')) {
      try {
        await apiEndpoints.vehicles.delete(vehicleId);
        // Refresh vehicles
        const vehiclesRes = await apiEndpoints.vehicles.list();
        setRecentVehicles(vehiclesRes.data);
        // Refresh summary
        const summaryRes = await apiEndpoints.dashboard.summary();
        setSummary(summaryRes.data);
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        alert('Error deleting vehicle. Please try again.');
      }
    }
  };

  const handleSelectDocument = (doc) => {
    setSelectedDocument(doc);
  };

  const handleBackToDocuments = () => {
    setSelectedDocument(null);
  };

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
  };

  const handleBackToVehicles = () => {
    setSelectedVehicle(null);
  };

  const handleSaveVehicleEvent = async (payload) => {
    try {
      await apiEndpoints.events.create(payload);
      setShowEventForm(false);
    } catch (err) {
      console.error('Failed to create event:', err);
      alert('Error saving event. Please try again.');
    }
  };

  const handleSaveVehicleDocument = async (payload) => {
    try {
      await apiEndpoints.documents.create(payload);
      setShowDocumentForm(false);
    } catch (err) {
      console.error('Failed to save document:', err);
      alert('Error saving document. Please try again.');
    }
  };

  const handleOpenDriverModal = async () => {
    try {
      const res = await apiEndpoints.drivers.list();
      setDrivers(res.data || []);
      setShowDriverModal(true);
    } catch (err) {
      console.error('Failed to load drivers:', err);
      alert('Error loading drivers. Please try again.');
    }
  };

  const handleAssignDriver = async (driverId) => {
    try {
      await apiEndpoints.assignments.create({ vehicleId: selectedVehicle.id || selectedVehicle._id, driverId });
      setShowDriverModal(false);
      const vehiclesRes = await apiEndpoints.vehicles.list();
      setRecentVehicles(vehiclesRes.data);
      const vid = selectedVehicle.id || selectedVehicle._id;
      const next = vehiclesRes.data.find((v) => (v.id || v._id) === vid);
      if (next) setSelectedVehicle(next);
    } catch (err) {
      console.error('Failed to assign driver:', err);
      alert('Error assigning driver. Please try again.');
    }
  };

  const handleSaveAccident = async (payload, file) => {
    try {
      await apiEndpoints.accidents.create(payload, file);
      setShowAccidentForm(false);
    } catch (err) {
      console.error('Failed to save accident:', err);
      alert('Error saving accident. Please try again.');
    }
  };

  const handleSaveCost = async (payload, file) => {
    try {
      await apiEndpoints.costs.create(payload, file);
      setShowCostForm(false);
    } catch (err) {
      console.error('Failed to save cost:', err);
      alert('Error saving cost. Please try again.');
    }
  };

  const handleDeallocate = async () => {
    if (!await confirmDialog('Remove the driver assignment from this vehicle?')) return;
    try {
      await apiEndpoints.assignments.create({ vehicleId: selectedVehicle.id || selectedVehicle._id, driverId: null });
      const vehiclesRes = await apiEndpoints.vehicles.list();
      setRecentVehicles(vehiclesRes.data);
      const vid = selectedVehicle.id || selectedVehicle._id;
      const next = vehiclesRes.data.find((v) => (v.id || v._id) === vid);
      if (next) setSelectedVehicle(next);
    } catch (err) {
      console.error('Failed to deallocate driver:', err);
      alert('Error deallocating driver. Please try again.');
    }
  };

  const handleArchiveVehicle = async () => {
    if (!await confirmDialog('Archive this vehicle? Its status will be set to Arhivat.')) return;
    try {
      await apiEndpoints.vehicles.update(selectedVehicle.id || selectedVehicle._id, { status: 'Arhivat' });
      setSelectedVehicle(null);
      const vehiclesRes = await apiEndpoints.vehicles.list();
      setRecentVehicles(vehiclesRes.data);
    } catch (err) {
      console.error('Failed to archive vehicle:', err);
      alert('Error archiving vehicle. Please try again.');
    }
  };

  const handleExportSingleVehicle = () => {
    const v = selectedVehicle;
    const headers = ['Plate', 'Brand', 'Model', 'Year', 'Fuel', 'Body Type', 'Color', 'Company', 'VIN', 'Driver', 'Status'];
    const row = [v.plate, v.brand, v.model, v.year, v.fuel, v.bodyType || v.type || '', v.color || '', v.company, v.vin || v.chassis || '', v.driverId || 'Available', v.status];
    const csv = [headers, row]
      .map((r) => r.map((val) => `"${String(val || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.setAttribute('download', `vehicle_${v.plate || 'export'}.csv`);
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadFile = (doc) => {
    const url = doc.fileUrl;
    if (!url) {
      alert('No file attached to this document.');
      return;
    }
    const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/${url.replace(/^\//, '')}`;
    const a = window.document.createElement('a');
    a.href = fullUrl;
    a.download = url.split('/').pop() || 'document';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const handleViewFile = (doc) => {
    const url = doc.fileUrl;
    if (!url) {
      alert('No file attached to this document.');
      return;
    }
    const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/${url.replace(/^\//, '')}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return 9999;
    const s = String(expiryDate);
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    const exp = m
      ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`)
      : new Date(s);
    if (Number.isNaN(exp.getTime())) return 9999;
    const now = new Date();
    return Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getExpiryState = (doc) => {
    const days = doc._days !== undefined ? doc._days : getDaysRemaining(doc.expiryDate);
    if (days < 0) return 'critical';
    if (days < 10) return 'warning';
    return 'good';
  };

  const getStatusColor = (state) => {
    if (state === 'critical') return '#ef4444';
    if (state === 'warning') return '#f59e0b';
    return '#10b981';
  };

  const getStatusLabel = (state) => {
    if (state === 'critical') return 'CRITICAL';
    if (state === 'warning') return 'Expire';
    return 'Valid';
  };

  const parseFlexibleDate = (value) => {
    if (!value) return null;
    const s = String(value);
    const m1 = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m1) return new Date(`${m1[3]}-${m1[2]}-${m1[1]}T00:00:00`);
    const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m2) return new Date(`${m2[1]}-${m2[2]}-${m2[3]}T00:00:00`);
    const parsed = new Date(s);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getEventCriticality = (event) => {
    const status = String(event.status || '').toLowerCase();
    if (
      status.includes('delay') ||
      status.includes('late') ||
      status.includes('pending') ||
      status.includes('intarziere') ||
      status.includes('critical')
    ) {
      return 'critical';
    }
    if (status.includes('finalizat') || status.includes('complete') || status.includes('done') || status.includes('finished')) {
      return 'good';
    }
    return 'active';
  };

  const criticalAlerts = alerts.filter((event) => getEventCriticality(event) === 'critical');
  const urgentCount = criticalAlerts.length;
  const alertText = urgentCount > 0
    ? `${urgentCount} critical alert${urgentCount === 1 ? '' : 's'} require attention`
    : 'No critical alerts right now.';

  if (loading) return <div className="page-loading">Loading dashboard...</div>;

  const fleetTotal = summary?.totalVehicles || 0;
  const fleetAllocated = summary?.allocatedVehicles || 0;
  const fleetFree = Math.max(0, fleetTotal - fleetAllocated);
  const utilPct = fleetTotal > 0 ? Math.round((fleetAllocated / fleetTotal) * 100) : 0;

  // Always recalculate days live from expiryDate — never trust stale daysRemaining in DB
  const withLiveDays = documents.map(d => ({ ...d, _days: getDaysRemaining(d.expiryDate) }));
  const docCrit     = withLiveDays.filter(d => d._days < 14).length;
  const docExpiring = withLiveDays.filter(d => d._days >= 14 && d._days < 30).length;
  const docHealthOk = withLiveDays.filter(d => d._days >= 30).length;
  const expiringDocs = docCrit + docExpiring;

  // Sort by urgency for banner and list
  const liveDocsSorted = withLiveDays.slice().sort((a, b) => a._days - b._days);
  const criticalDocsLive = liveDocsSorted.filter(d => d._days < 14);
  const bannerDoc = criticalDocsLive[0] || null;
  const criticalCount = criticalDocsLive.length;

  const criticalDocuments = liveDocsSorted.filter(d => d._days < 30).slice(0, 4);

  const activeEvents = alerts.filter((event) => getEventCriticality(event) === 'active').slice(0, 4);

  const getDocumentStatusColor = (days) => {
    if (days < 0) return '#ef4444';
    if (days < 10) return '#ef4444';
    if (days < 30) return '#f59e0b';
    return '#10b981';
  };

  const getDocumentStatusLabel = (days) => {
    if (days < 0) return 'CRITICAL';
    if (days < 10) return 'CRITICAL';
    if (days < 30) return 'Expire';
    return 'Valid';
  };

  const getAvatar = (name) => {
    if (!name) return 'UN';
    return name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const s = String(value);
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    const dt = m
      ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`)
      : new Date(s);
    return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
  };

  if (loading) return <div className="page-loading">Loading dashboard...</div>;

  // Show selected document detail view
  if (selectedDocument) {
    const expiryState = getExpiryState(selectedDocument);
    const statusColor = getStatusColor(expiryState);
    const daysLeft = selectedDocument._days !== undefined ? selectedDocument._days : getDaysRemaining(selectedDocument.expiryDate);
    const statusLabel = getStatusLabel(expiryState);
    const plateLabel = selectedDocument.plate || selectedDocument.vehicle || selectedDocument.registration || 'UNKNOWN';
    const issuer = selectedDocument.issuer || selectedDocument.company || '—';
    const issueDate = selectedDocument.issueDate ? parseFlexibleDate(selectedDocument.issueDate)?.toLocaleDateString() : '—';
    const expiryDate = parseFlexibleDate(selectedDocument.expiryDate)?.toLocaleDateString() || '—';

    return (
      <div>
        <button className="back-btn" onClick={handleBackToDocuments}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to dashboard
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span className="plate-pill">{plateLabel}</span>
          </div>
          <div className="vprofile-model">{selectedDocument.documentType || 'Document'}</div>
          <div className="vprofile-sub">{issuer} • expires {expiryDate}</div>
        </div>

        <div className="detail-grid">
          <div className="detail-left">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Document Details</div>
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/documents')}>View All</button>
              </div>
              <div className="card-body">
                <div className="info-row"><div className="info-lbl">Vehicle</div><div className="info-val">{plateLabel}</div></div>
                <div className="info-row"><div className="info-lbl">Document type</div><div className="info-val">{selectedDocument.documentType || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Issuer</div><div className="info-val">{issuer}</div></div>
                <div className="info-row"><div className="info-lbl">Issued on</div><div className="info-val">{issueDate}</div></div>
                <div className="info-row"><div className="info-lbl">Expires on</div><div className="info-val">{expiryDate}</div></div>
                <div className="info-row"><div className="info-lbl">Days left</div><div className="info-val" style={{ color: statusColor }}>{daysLeft < 0 ? `${Math.abs(daysLeft)} days ago` : `${daysLeft} days`}</div></div>
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <div className="info-lbl">Status</div>
                  <div className="info-val">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', backgroundColor: expiryState === 'critical' ? 'rgba(239, 68, 68, 0.12)' : expiryState === 'warning' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.12)', color: statusColor, fontWeight: 700 }}>
                      ● {statusLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="detail-right">
            <div className="card quick-actions-card">
              <div className="card-header"><div className="card-title">Quick Actions</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => navigate('/documents')}>Renew Document</button>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => handleDownloadFile(selectedDocument)} disabled={!selectedDocument.fileUrl}>Download</button>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => handleViewFile(selectedDocument)} disabled={!selectedDocument.fileUrl}>View File</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show selected vehicle detail view
  if (selectedVehicle) {
    const plate = selectedVehicle.plate || selectedVehicle.registration || 'N/A';
    const driverName = selectedVehicle.driverName || selectedVehicle.driver || 'Unassigned';
    const driverRole = selectedVehicle.driverRole || 'Fleet driver';
    const activeEvents = selectedVehicle.events || [];
    const rca = selectedVehicle.docs?.rca;
    const itp = selectedVehicle.docs?.itp;
    const casco = selectedVehicle.docs?.casco;

    return (
      <div>
        <button className="back-btn" onClick={handleBackToVehicles}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to dashboard
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span className="plate-pill">{plate}</span>
          </div>
          <div className="vprofile-model">{selectedVehicle.brand} {selectedVehicle.model}</div>
          <div className="vprofile-sub">{selectedVehicle.year} • {selectedVehicle.fuel || '—'} • {selectedVehicle.company || '—'}</div>
        </div>

        <div className="detail-grid">
          <div className="detail-left">
            {/* Vehicle Details */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Vehicle Details</div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleEditVehicle(selectedVehicle)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteVehicle(selectedVehicle.id)}>Delete</button>
                  </div>
                )}
              </div>
              <div className="card-body">
                <div className="info-row"><div className="info-lbl">Registration</div><div className="info-val">{plate}</div></div>
                <div className="info-row"><div className="info-lbl">Brand / Model</div><div className="info-val">{selectedVehicle.brand} {selectedVehicle.model}</div></div>
                <div className="info-row"><div className="info-lbl">Year</div><div className="info-val">{selectedVehicle.year || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Fuel type</div><div className="info-val">{selectedVehicle.fuel || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Body type</div><div className="info-val">{selectedVehicle.bodyType || selectedVehicle.type || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Color</div><div className="info-val">{selectedVehicle.color || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Company</div><div className="info-val">{selectedVehicle.company || '—'}</div></div>
                <div className="info-row" style={{ borderBottom: 'none' }}><div className="info-lbl">VIN / Chassis</div><div className="info-val">{selectedVehicle.vin || selectedVehicle.chassis || '—'}</div></div>
              </div>
            </div>

            {/* Acts & Documents */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Acts & Documents</div>
                {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => { setEditingDocument(null); setShowDocumentForm(true); }}>+ Add</button>}
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {[{ label: 'RCA', sublabel: 'Compulsory Insurance', doc: rca, type: 'RCA' }, { label: 'ITP', sublabel: 'Technical Inspection', doc: itp, type: 'ITP' }, { label: 'CASCO', sublabel: 'Optional Insurance', doc: casco, type: 'CASCO' }].map((item, idx, arr) => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, background: item.doc ? '#eff6ff' : '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: item.doc ? '#3b82f6' : '#ef4444' }}>{item.label}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-1)' }}>{item.label} — {item.sublabel}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>
                        {item.doc ? `Expires ${item.doc.exp || item.doc.expiryDate || '—'}${item.doc.issuer ? ` • ${item.doc.issuer}` : ''}` : 'No record'}
                      </div>
                    </div>
                    {isAdmin && (
                      <button className="btn btn-ghost btn-xs" style={{ flexShrink: 0, color: '#059669' }} onClick={() => {
                        const fullDoc = documents.find((d) => d.plate === selectedVehicle.plate && (d.documentType || '').toUpperCase() === item.type);
                        setEditingDocument(fullDoc || { plate: selectedVehicle.plate, documentType: item.type });
                        setShowDocumentForm(true);
                      }}>Renew</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Active Events */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Active Events</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowEventForm(true)}>+ Event</button>
              </div>
              <div className="card-body">
                {activeEvents.length > 0 ? activeEvents.map((event, i) => (
                  <div key={event._id || event.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', padding: '10px 0', borderBottom: i < activeEvents.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.evt || event.name || 'Event'}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>{event.plate || plate}</div>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: '2px' }}>{event.created || event.startDate || '—'}</div>
                  </div>
                )) : (
                  <div style={{ padding: '12px 0', color: 'var(--text-3)', fontSize: '13px' }}>No active events.</div>
                )}
              </div>
            </div>
          </div>

          <div className="detail-right">
            {/* Quick Actions */}
            <div className="card quick-actions-card">
              <div className="card-header"><div className="card-title">Quick Actions</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowEventForm(true)}>Add Event</button>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowAccidentForm(true)}>Report Accident</button>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowStatsModal(true)}>See Statistics</button>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleExportSingleVehicle}>Export Data</button>
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowDocumentForm(true)}>Add Documents</button>}
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleOpenDriverModal}>Allocate / Change Driver</button>}
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowCostForm(true)}>Add Cost</button>}
                {isAdmin && <button className="btn btn-danger" style={{ width: '100%' }} onClick={handleArchiveVehicle}>Archive the Vehicle</button>}
              </div>
            </div>

            {/* Costs */}
            <CostsCard costs={vehicleCosts} onAdd={() => setShowCostForm(true)} />
          </div>
        </div>

        <EventForm
          isOpen={showEventForm}
          onClose={() => setShowEventForm(false)}
          event={{ plate: selectedVehicle.plate || '', car: `${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim() }}
          onSave={handleSaveVehicleEvent}
        />

        <DocumentForm
          isOpen={showDocumentForm}
          onClose={() => setShowDocumentForm(false)}
          document={editingDocument || { plate: selectedVehicle.plate || '' }}
          onSave={handleSaveVehicleDocument}
        />

        <VehicleForm
          isOpen={showVehicleForm}
          onClose={() => setShowVehicleForm(false)}
          vehicle={editingVehicle}
          onSave={handleSaveVehicle}
        />

        <AccidentForm
          isOpen={showAccidentForm}
          onClose={() => setShowAccidentForm(false)}
          defaultPlate={selectedVehicle.plate || ''}
          defaultCompany={selectedVehicle.company || ''}
          defaultVehicleName={`${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim()}
          onSave={handleSaveAccident}
        />

        <CostForm
          isOpen={showCostForm}
          onClose={() => setShowCostForm(false)}
          cost={{ plate: selectedVehicle.plate || '', company: selectedVehicle.company || '', vehicleName: `${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim() }}
          onSave={handleSaveCost}
        />

        <Modal isOpen={showStatsModal} onClose={() => setShowStatsModal(false)} title="Vehicle Statistics" size="md">
          <VehicleStatsContent vehicle={selectedVehicle} />
        </Modal>

        <Modal isOpen={showDriverModal} onClose={() => setShowDriverModal(false)} title="Select Driver" size="md">
          <div>
            {drivers.length === 0 ? (
              <p style={{ color: 'var(--text-3)', textAlign: 'center', padding: '20px 0' }}>No drivers available.</p>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {drivers.map((driver) => (
                  <button
                    key={driver._id || driver.id}
                    className="btn btn-secondary"
                    style={{ width: '100%', textAlign: 'left', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}
                    onClick={() => handleAssignDriver(driver.id || driver._id)}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#3b82f6', color: 'white', fontSize: '13px', fontWeight: '600', flexShrink: 0 }}>
                      {(driver.name || 'D').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--text-1)' }}>{driver.name || 'Unknown'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>{driver.phone || driver.email || driver.license || ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Modal>
      </div>
    );
  }

  // Company costs detail view
  if (selectedCompany) {
    const companyCosts = allCosts.filter((c) => companyKey(c.company) === companyKey(selectedCompany));
    const companyTotal = companyCosts.reduce((s, c) => s + (Number(c.amount) || 0), 0);

    const byType = {};
    companyCosts.forEach((c) => {
      const t = c.costType || 'Other';
      byType[t] = (byType[t] || 0) + (Number(c.amount) || 0);
    });
    const typeEntries = Object.entries(byType).sort((a, b) => b[1] - a[1]);
    const typeSegments = typeEntries.map(([t, v]) => ({ value: v, color: COST_TYPE_COLORS[t] || '#9ca3af' }));

    const byVehicle = {};
    companyCosts.forEach((c) => {
      const p = c.plate || 'Unknown';
      if (!byVehicle[p]) byVehicle[p] = { plate: p, vehicleName: c.vehicleName || '', byType: {}, total: 0 };
      const t = c.costType || 'Other';
      byVehicle[p].byType[t] = (byVehicle[p].byType[t] || 0) + (Number(c.amount) || 0);
      byVehicle[p].total += Number(c.amount) || 0;
    });
    const vehicleRows = Object.values(byVehicle).sort((a, b) => b.total - a.total);
    const maxVehicleTotal = vehicleRows[0]?.total || 1;

    return (
      <div>
        <button className="back-btn" onClick={() => setSelectedCompany(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to dashboard
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', borderRadius: '12px', backgroundColor: '#0077b6', color: '#fff', fontSize: '20px', fontWeight: 800 }}>
              {selectedCompany.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div className="vprofile-model">{selectedCompany}</div>
          <div className="vprofile-sub">Total spend: {companyTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} RON • {companyCosts.length} entries</div>
        </div>

        <div className="detail-grid">
          {/* Cost by type — donut */}
          <div className="card">
            <div className="card-header"><div className="card-title">Cost by Type</div></div>
            <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
                <DonutChart segments={typeSegments} size={110} strokeWidth={13} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-1)' }}>{fmtAmt(companyTotal)}</span>
                  <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>RON total</span>
                </div>
              </div>
              <div style={{ flex: 1, display: 'grid', gap: '8px' }}>
                {typeEntries.map(([type, amt]) => {
                  const pct = companyTotal > 0 ? Math.round((amt / companyTotal) * 100) : 0;
                  const color = COST_TYPE_COLORS[type] || '#9ca3af';
                  return (
                    <div key={type}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', fontWeight: 600, color: 'var(--text-1)' }}>
                          <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0, boxShadow: `0 0 5px ${color}80` }} />
                          {type}
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color }}>{amt.toLocaleString(undefined, { minimumFractionDigits: 0 })} RON <span style={{ color: '#9ca3af', fontWeight: 400 }}>({pct}%)</span></span>
                      </div>
                      <div style={{ height: 4, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Cost by vehicle */}
          <div className="card">
            <div className="card-header"><div className="card-title">By Vehicle</div></div>
            <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              {vehicleRows.map((veh) => {
                const pct = Math.round((veh.total / maxVehicleTotal) * 100);
                const topType = Object.entries(veh.byType).sort((a, b) => b[1] - a[1])[0];
                const barColor = topType ? (COST_TYPE_COLORS[topType[0]] || '#0077b6') : '#0077b6';
                return (
                  <div key={veh.plate}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="plate" style={{ fontSize: '11px' }}>{veh.plate}</span>
                        {veh.vehicleName && <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>{veh.vehicleName}</span>}
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-1)' }}>{veh.total.toLocaleString(undefined, { minimumFractionDigits: 0 })} RON</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 5, transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                      {Object.entries(veh.byType).sort((a, b) => b[1] - a[1]).map(([t, v]) => (
                        <span key={t} style={{ fontSize: '10px', color: COST_TYPE_COLORS[t] || '#9ca3af', fontWeight: 600, background: `${COST_TYPE_COLORS[t] || '#9ca3af'}15`, padding: '1px 6px', borderRadius: 4 }}>
                          {t}: {v.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
              {vehicleRows.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: '13px' }}>No cost data.</div>}
            </div>
          </div>
        </div>

        {/* Full costs table */}
        <div className="card" style={{ marginTop: '4px' }}>
          <div className="card-header">
            <div className="card-title">All Expenses</div>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{companyCosts.length} entries</span>
          </div>
          <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
            <table style={{ width: '100%', minWidth: '520px', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['PLATE / VEHICLE', 'DATE', 'TYPE', 'DESCRIPTION', 'AMOUNT'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companyCosts.sort((a, b) => new Date(b.date) - new Date(a.date)).map((c, i) => {
                  const color = COST_TYPE_COLORS[c.costType] || '#9ca3af';
                  return (
                    <tr key={c._id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <span className="plate" style={{ fontSize: '11px' }}>{c.plate}</span>
                        {c.vehicleName && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>{c.vehicleName}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-3)' }}>{c.date ? new Date(c.date).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color, backgroundColor: `${color}18` }}>{c.costType || '—'}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-3)', maxWidth: '220px' }}>{c.description || '—'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0077b6', whiteSpace: 'nowrap' }}>{Number(c.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {c.currency || 'RON'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (showAllCostsSummary) {
    const allGrandTotal = allCosts.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    // Case/whitespace-insensitive grouping — "Meridian Tech SRL" and
    // "MERIDIAN TECH SRL" are the same company.
    const companyRows = groupCostsByCompany(allCosts, companies.map((co) => co.label || co.name))
      .sort((a, b) => b.total - a.total);
    const maxCoTotal = companyRows[0]?.total || 1;

    return (
      <div>
        <button className="back-btn" onClick={() => setShowAllCostsSummary(false)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Back to dashboard
        </button>
        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', borderRadius: '12px', backgroundColor: '#0077b6', color: '#fff', fontSize: '22px', fontWeight: 800 }}>₴</span>
          </div>
          <div className="vprofile-model">All Fleet Costs</div>
          <div className="vprofile-sub">Total: {allGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} RON · {companyRows.length} companies · {allCosts.length} entries</div>
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">Costs by Company</div></div>
          <div className="card-body" style={{ display: 'grid', gap: '16px' }}>
            {companyRows.map((co) => {
              const pct = Math.round((co.total / maxCoTotal) * 100);
              const topType = Object.entries(co.byType).sort((a, b) => b[1] - a[1])[0];
              const barColor = topType ? (COST_TYPE_COLORS[topType[0]] || '#0077b6') : '#0077b6';
              return (
                <div key={co.name} style={{ cursor: 'pointer', padding: '4px 0' }} onClick={() => { setShowAllCostsSummary(false); setSelectedCompany(co.name); }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-1)' }}>{co.name}</div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#0077b6' }}>{co.total.toLocaleString(undefined, { minimumFractionDigits: 2 })} RON</div>
                  </div>
                  <div style={{ height: 6, borderRadius: 6, background: '#f1f5f9', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 6, transition: 'width 0.8s ease' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
                    {Object.entries(co.byType).sort((a, b) => b[1] - a[1]).map(([t, v]) => (
                      <span key={t} style={{ fontSize: '10px', color: COST_TYPE_COLORS[t] || '#9ca3af', fontWeight: 600, background: `${COST_TYPE_COLORS[t] || '#9ca3af'}15`, padding: '1px 6px', borderRadius: 4 }}>
                        {t}: {v.toLocaleString(undefined, { minimumFractionDigits: 0 })} RON
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="card" style={{ marginTop: '16px' }}>
          <div className="card-header">
            <div className="card-title">All Expenses</div>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{allCosts.length} entries</span>
          </div>
          <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['PLATE', 'COMPANY', 'DATE', 'TYPE', 'DESCRIPTION', 'AMOUNT'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 16px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...allCosts].sort((a, b) => new Date(b.date) - new Date(a.date)).map((c, i) => {
                  const color = COST_TYPE_COLORS[c.costType] || '#9ca3af';
                  return (
                    <tr key={c._id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 16px' }}><span className="plate" style={{ fontSize: '11px' }}>{c.plate}</span></td>
                      <td style={{ padding: '10px 16px', color: 'var(--text-3)', fontSize: '12px' }}>{c.company || '—'}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--text-3)' }}>{c.date ? new Date(c.date).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '10px 16px' }}><span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color, backgroundColor: `${color}18` }}>{c.costType || '—'}</span></td>
                      <td style={{ padding: '10px 16px', color: 'var(--text-3)' }}>{c.description || '—'}</td>
                      <td style={{ padding: '10px 16px', fontWeight: 700, color: '#0077b6', whiteSpace: 'nowrap' }}>{Number(c.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {c.currency || 'RON'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Hello, {user?.name || 'Fleet Manager'} 👋</div>
          <div className="ph-sub">{criticalCount > 0 ? `${criticalCount} critical alert${criticalCount === 1 ? '' : 's'} require attention` : 'All documents are up to date.'}</div>
        </div>
        {isAdmin && <button className="btn btn-primary btn-sm" onClick={handleAddVehicle}>
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add Vehicle
        </button>}
      </div>

      <div className="dashboard-banners">
        {criticalCount > 0 && bannerDoc && (
          <div className="dashboard-banner critical" style={{ cursor: 'pointer' }} onClick={() => navigate('/alerts')}>
            <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            CRITICAL: {bannerDoc.documentType} — {bannerDoc.plate} {bannerDoc._days < 0 ? `expired ${Math.abs(bannerDoc._days)} days ago` : `expires in ${bannerDoc._days} day${bannerDoc._days === 1 ? '' : 's'}`} — act now.
          </div>
        )}
        {expiringDocs > 0 && (
          <div className="dashboard-banner warn" style={{ cursor: 'pointer' }} onClick={() => navigate('/alerts')}>
            <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9V9h2v4zm0-6H9V5h2v2z" />
            </svg>
            {expiringDocs} car documents expire in the next 30 days — ITP, Casco and Rovinieta require renewed.
          </div>
        )}
      </div>

      {/* Company Cost Chart */}
      {allCosts.length > 0 && (() => {
        const grandTotal = allCosts.reduce((s, c) => s + (Number(c.amount) || 0), 0);
        const allTypes = [...new Set(allCosts.map((c) => c.costType).filter(Boolean))];

        // Build from allCosts directly, grouped case/whitespace-insensitively so
        // "Meridian Tech SRL" and "MERIDIAN TECH SRL" land in the same bucket.
        const costMap = {};
        groupCostsByCompany(allCosts, companies.map((co) => co.label || co.name))
          .forEach((bucket) => { costMap[bucket.name] = bucket; });

        // Strip legal suffixes for fuzzy matching (e.g. "Meridian Tech SRL" → "meridian tech")
        const stripLegal = (s) => s.replace(/\s+(S\.?R\.?L\.?|S\.?A\.?|IFN|SRA|RA|SNC|SCS|PFA)\.?\s*$/i, '').toLowerCase().trim();

        // Also include companies from the API that have no costs yet (shows empty bar)
        companies.forEach((co) => {
          const name = (co.label || co.name || '').trim();
          if (!name) return;
          const alreadyIn = Object.keys(costMap).find(
            (k) => stripLegal(k) === stripLegal(name)
          );
          if (!alreadyIn) costMap[name] = { name, total: 0, byType: {} };
        });

        const companyData = Object.values(costMap).sort((a, b) => b.total - a.total);

        const shortLabels = shortCompanyLabels(companyData.map((d) => d.name));

        return (
          <div className="dash-costs-grid">
            <CompanyCostChart
              companyData={companyData}
              allTypes={allTypes}
              grandTotal={grandTotal}
              onCompanyClick={(name) => setSelectedCompany(name)}
            />
            <div
              className="stat-card"
              style={{ padding: '20px', borderTop: '3px solid #0077b6', cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.15s ease, transform 0.15s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,119,182,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
              onClick={() => setShowAllCostsSummary(true)}
            >
              <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#9ca3af', marginBottom: '10px' }}>Total Fleet Costs</div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--text-1)', lineHeight: 1 }}>{grandTotal.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px', marginBottom: '14px' }}>RON total</div>
              <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '14px' }}>
                {companyData.filter((d) => d.total > 0).length} companies · {allCosts.length} entries
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', flex: 1 }}>
                {companyData.filter((d) => d.total > 0).slice(0, 5).map((co) => (
                  <div key={co.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-2)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '110px' }}>{shortLabels.get(co.name) || co.name}</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-1)', flexShrink: 0, marginLeft: '4px' }}>{co.total.toLocaleString()}</span>
                    </div>
                    <div style={{ height: 3, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(co.total / grandTotal) * 100}%`, background: '#0077b6', borderRadius: 3, transition: 'width 0.8s ease' }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '16px', fontSize: '11px', color: '#0077b6', fontWeight: 600 }}>View all details →</div>
            </div>
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <ChartCard
          title="Fleet Status"
          borderColor="#10b981"
          segments={[{ value: fleetAllocated, color: '#10b981' }, { value: fleetFree, color: '#93c5fd' }]}
          center={{ value: fleetTotal, label: 'total' }}
          legend={[{ label: 'Allocated', value: fleetAllocated, color: '#10b981' }, { label: 'Available', value: fleetFree, color: '#93c5fd' }]}
        />
        <ChartCard
          title="Fleet Utilization"
          borderColor="#3b82f6"
          segments={[{ value: utilPct, color: '#3b82f6' }, { value: Math.max(0, 100 - utilPct), color: '#bfdbfe' }]}
          center={{ value: `${utilPct}%`, label: 'in use' }}
          legend={[{ label: 'Active', value: fleetAllocated, color: '#3b82f6' }, { label: 'Idle', value: fleetFree, color: '#93c5fd' }]}
        />
        <ChartCard
          title="Document Health"
          borderColor="#f59e0b"
          segments={[{ value: docCrit, color: '#ef4444' }, { value: docExpiring, color: '#f59e0b' }, { value: docHealthOk, color: '#10b981' }]}
          center={{ value: documents.length, label: 'docs' }}
          legend={[{ label: 'Critical', value: docCrit, color: '#ef4444' }, { label: 'Expiring', value: docExpiring, color: '#f59e0b' }, { label: 'Valid', value: docHealthOk, color: '#10b981' }]}
        />
        <ChartCard
          title="Alert Severity"
          borderColor="#ef4444"
          segments={[{ value: docCrit, color: '#ef4444' }, { value: docExpiring, color: '#f59e0b' }, { value: docHealthOk, color: '#e2e8f0' }]}
          center={{ value: docCrit + docExpiring, label: 'alerts', valueColor: docCrit > 0 ? '#ef4444' : undefined }}
          legend={[{ label: 'Critical', value: docCrit, color: '#ef4444' }, { label: 'Attention', value: docExpiring, color: '#f59e0b' }, { label: 'OK', value: docHealthOk, color: '#94a3b8' }]}
        />
      </div>

      <div className="dash-two-col">
        {/* Urgent Documents */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Urgent Documents</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/alerts')}>See all —</button>
          </div>
          <div className="card-body">
            {criticalDocuments.length > 0 ? criticalDocuments.map((doc) => (
              <div key={doc._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => handleSelectDocument(doc)}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                      {doc.plate}
                    </span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{doc.documentType}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{doc.issuer}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    expires {formatDate(doc.expiryDate)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: getDocumentStatusColor(doc.daysRemaining) }}>
                    {Math.max(doc.daysRemaining || 0, 0)}
                  </div>
                  <div style={{ fontSize: '11px', color: getDocumentStatusColor(doc.daysRemaining), fontWeight: '600', marginTop: '2px' }}>
                    {getDocumentStatusLabel(doc.daysRemaining)} {doc.daysRemaining !== 1 ? 'days' : 'day'}
                  </div>
                </div>
                <div style={{ marginLeft: '12px' }}>
                  <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: getDocumentStatusColor(doc.daysRemaining), backgroundColor: doc.daysRemaining < 30 ? '#fef2f2' : '#ecfdf5' }}>
                    ● {getDocumentStatusLabel(doc.daysRemaining).toUpperCase()}
                  </span>
                </div>
              </div>
            )) : (
              <div style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>No urgent documents.</div>
            )}
          </div>
        </div>

        {/* Active Events */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Active Events</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/events')}>All —</button>
          </div>
          <div className="card-body">
            {activeEvents.length > 0 ? activeEvents.map((event) => {
              const completionDate = event.estEnd || event.created;
              const s = String(completionDate || '');
              const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
              const eventDate = m
                ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`)
                : new Date(s);
              return (
                <div
                  key={event._id}
                  onClick={() => navigate('/events', { state: { selectedEventId: event._id } })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/events', { state: { selectedEventId: event._id } }); } }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                        {event.plate}
                      </span>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>{event.evt}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{formatDate(event.created)}</div>
                      </div>
                    </div>
                  </div>
                  <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', backgroundColor: '#eff6ff', color: '#3b82f6' }}>
                    Active
                  </span>
                </div>
              );
            }) : (
              <div style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>No active events.</div>
            )}
          </div>
        </div>
      </div>

      {/* Fleet Quick View Table */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="card-title">Fleet — Quick View</div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/vehicles')}>Manage fleet —</button>
        </div>
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '560px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em' }}>NUMBER</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em' }}>VEHICLE</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em' }}>ASSIGNED DRIVER</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em' }}>RCA</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em' }}>ITP</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em' }}>CASCO</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: '600', fontSize: '11px', letterSpacing: '0.05em' }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((vehicle) => {
                const driverName = vehicle.driverName || vehicle.driver || 'Unassigned';
                const isUnassigned = driverName === 'Unassigned';

                const docCell = (doc) => {
                  if (!doc?.exp) return { date: '—', label: 'No file', color: '#9ca3af' };
                  const expDate = parseFlexibleDate(doc.exp);
                  const daysLeft = expDate ? Math.ceil((expDate - new Date()) / 86400000) : null;
                  const color = daysLeft === null ? '#9ca3af' : daysLeft < 0 ? '#ef4444' : daysLeft < 30 ? '#f59e0b' : '#10b981';
                  const label = daysLeft === null ? '—' : daysLeft < 0 ? 'Expired' : daysLeft < 30 ? 'Expiring' : 'Valid';
                  return { date: doc.exp, label, color };
                };

                const rca   = docCell(vehicle.docs?.rca);
                const itp   = docCell(vehicle.docs?.itp);
                const casco = docCell(vehicle.docs?.casco);

                const statusColor = vehicle.status === 'Alocat' ? { bg: '#dcfce7', fg: '#16a34a' }
                  : vehicle.status === 'Service' ? { bg: '#fef9c3', fg: '#ca8a04' }
                  : vehicle.status === 'Arhivat' ? { bg: '#f3f4f6', fg: '#6b7280' }
                  : { bg: '#dbeafe', fg: '#0284c7' };

                return (
                  <tr key={vehicle._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => handleSelectVehicle(vehicle)}>
                    <td style={{ padding: '12px' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '4px', fontWeight: '600' }}>
                        {vehicle.plate || vehicle.registration || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{vehicle.brand} {vehicle.model || ''}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{vehicle.year || '—'}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', backgroundColor: isUnassigned ? '#e5e7eb' : '#3b82f6', color: isUnassigned ? '#9ca3af' : 'white', fontSize: '11px', fontWeight: '600' }}>
                          {isUnassigned ? '—' : getAvatar(driverName)}
                        </span>
                        <span style={{ fontSize: '13px', color: isUnassigned ? '#9ca3af' : 'var(--text-1)' }}>{driverName}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px' }}>
                      <span style={{ color: rca.color }}>{rca.date}</span>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{rca.label}</div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px' }}>
                      <span style={{ color: itp.color }}>{itp.date}</span>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{itp.label}</div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px' }}>
                      <span style={{ color: casco.color }}>{casco.date}</span>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{casco.label}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: statusColor.bg, color: statusColor.fg, fontSize: '11px', fontWeight: '600' }}>
                        {vehicle.status || 'Disponibil'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <VehicleForm
        isOpen={showVehicleForm}
        onClose={() => setShowVehicleForm(false)}
        vehicle={editingVehicle}
        onSave={handleSaveVehicle}
      />
    </div>
  );
};

export default Dashboard;
